/**
 * Compact encounters/visits timeline for the patient sidebar "Visits" tab.
 */
import { useMemo } from 'react';
import { NavIcon } from './shared';
import { usePowerSyncQuery } from '../adapters/powersync';
import {
  qEncountersByPatient,
  qEncounterSummaryCounts,
  qEncounterVitalsSnapshot,
  qEncounterNotesPreviews,
  type PatientEncounterRow,
  type EncounterSummaryCountRow,
  type EncounterVitalsSnapshotRow,
  type EncounterNotePreviewRow,
} from '@helios/data-access';

// ─── Helpers ───

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function fmtTime(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

const TYPE_LABELS: Record<string, string> = {
  consultation: 'Consultation',
  procedure: 'Procedure',
  follow_up: 'Follow-up',
  triage: 'Triage',
};

const TYPE_COLORS: Record<string, { bg: string; color: string }> = {
  consultation: { bg: '#dbeafe', color: '#2563eb' },
  procedure: { bg: '#fef3c7', color: '#d97706' },
  follow_up: { bg: '#f3e8ff', color: '#7c3aed' },
  triage: { bg: '#fecaca', color: '#dc2626' },
};

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  open: { bg: '#dcfce7', color: '#16a34a' },
  closed: { bg: '#f3f4f6', color: '#6b7280' },
};

function IndicatorDot({ icon, count }: { icon: string; count: number; label: string }) {
  if (count === 0) return null;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        padding: '0 5px',
        borderRadius: 'var(--radius-xs)',
        background: 'var(--bg-surface-2, #f5f5f5)',
        fontSize: 9,
        color: 'var(--text-muted)',
        fontWeight: 500,
        lineHeight: '16px',
      }}
    >
      <NavIcon name={icon} /> {count}
    </span>
  );
}

// ─── Component ───

export function SidebarVisitsPanel({
  patientId,
  selectedEncounterId,
  onSelectEncounter,
}: {
  patientId: string;
  selectedEncounterId?: string;
  onSelectEncounter: (id: string) => void;
}) {
  const { data: encounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    qEncountersByPatient(patientId),
  );
  const { data: summaryCounts = [] } = usePowerSyncQuery<EncounterSummaryCountRow>(
    qEncounterSummaryCounts(patientId),
  );
  const { data: vitalsSnapshot = [] } = usePowerSyncQuery<EncounterVitalsSnapshotRow>(
    qEncounterVitalsSnapshot(patientId),
  );
  const { data: notesPreviews = [] } = usePowerSyncQuery<EncounterNotePreviewRow>(
    qEncounterNotesPreviews(patientId),
  );

  const sorted = useMemo(
    () =>
      [...encounterRows].sort((a, b) => {
        const da = a.started_at ?? a.created_at ?? '';
        const db = b.started_at ?? b.created_at ?? '';
        return new Date(db).getTime() - new Date(da).getTime();
      }),
    [encounterRows],
  );

  const countsMap = useMemo(() => {
    const map: Record<string, EncounterSummaryCountRow> = {};
    for (const r of summaryCounts) map[r.encounter_id] = r;
    return map;
  }, [summaryCounts]);

  const vitalsMap = useMemo(() => {
    const map: Record<string, Record<string, { value: number | null; unit: string | null }>> = {};
    for (const r of vitalsSnapshot) {
      if (!map[r.encounter_id]) map[r.encounter_id] = {};
      map[r.encounter_id][r.code] = { value: r.value_number, unit: r.unit };
    }
    return map;
  }, [vitalsSnapshot]);

  const notesMap = useMemo(() => {
    const map: Record<string, EncounterNotePreviewRow[]> = {};
    for (const r of notesPreviews) {
      if (!map[r.encounter_id]) map[r.encounter_id] = [];
      if (map[r.encounter_id].length < 2) map[r.encounter_id].push(r);
    }
    return map;
  }, [notesPreviews]);

  function getHighlight(encId: string): string | null {
    const v = vitalsMap[encId];
    if (v) {
      const parts: string[] = [];
      if (v.systolic?.value != null && v.diastolic?.value != null)
        parts.push(`BP ${v.systolic.value}/${v.diastolic.value}`);
      if (v.pulse?.value != null) parts.push(`HR ${v.pulse.value}`);
      if (parts.length > 0) return parts.join(' · ');
    }
    const c = countsMap[encId];
    if (c) {
      if (c.vitals_count > 0) return 'Vitals recorded';
      if (c.notes_count > 0) return `${c.notes_count} note${c.notes_count > 1 ? 's' : ''}`;
      if (c.files_count > 0) return `${c.files_count} file${c.files_count > 1 ? 's' : ''}`;
    }
    return null;
  }

  if (sorted.length === 0) {
    return (
      <div
        style={{
          padding: 'var(--sp-6)',
          textAlign: 'center',
          color: 'var(--text-placeholder)',
          fontSize: 'var(--font-size-sm)',
        }}
      >
        No visits yet
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', paddingLeft: 20 }}>
      {/* Vertical timeline line */}
      <div
        style={{
          position: 'absolute',
          left: 5,
          top: 8,
          bottom: 8,
          width: 2,
          background: 'var(--border)',
          borderRadius: 1,
        }}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
        {sorted.map((enc) => {
          const isSelected = selectedEncounterId === enc.id;
          const isOpen = enc.status === 'open';
          const typeLabel = TYPE_LABELS[enc.type] ?? enc.type ?? 'Visit';
          const typeColor = TYPE_COLORS[enc.type] ?? { bg: '#f3f4f6', color: '#6b7280' };
          const statusColor = STATUS_COLORS[enc.status] ?? STATUS_COLORS.closed;
          const highlight = getHighlight(enc.id);
          const counts = countsMap[enc.id];

          return (
            <div key={enc.id} style={{ position: 'relative' }}>
              {/* Timeline dot */}
              <div
                style={{
                  position: 'absolute',
                  left: -18,
                  top: 14,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: isOpen ? 'var(--color-primary, #2563eb)' : 'var(--text-placeholder)',
                  border: '2px solid var(--bg-surface)',
                  boxShadow: isOpen ? '0 0 0 3px rgba(37, 99, 235, 0.12)' : undefined,
                  zIndex: 1,
                }}
              />
              <button
                onClick={() => onSelectEncounter(enc.id)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  padding: 'var(--sp-3)',
                  borderRadius: 'var(--radius-md)',
                  border: isSelected
                    ? '2px solid var(--color-primary, #92400e)'
                    : `1px solid ${isOpen ? '#fde68a' : 'var(--border)'}`,
                  background: isSelected
                    ? 'var(--color-primary-subtle, #fffbeb)'
                    : 'var(--bg-surface)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  width: '100%',
                }}
              >
                {/* Currently viewing indicator */}
                {isSelected && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 9,
                      fontWeight: 600,
                      color: 'var(--color-primary, #2563eb)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 2,
                    }}
                  >
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                    This Visit Note
                  </div>
                )}

                {/* Row 1: Type + Status chips */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span
                    style={{
                      fontSize: 10,
                      padding: '1px 7px',
                      borderRadius: 'var(--radius-full)',
                      background: typeColor.bg,
                      color: typeColor.color,
                      fontWeight: 600,
                    }}
                  >
                    {typeLabel}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      padding: '1px 7px',
                      borderRadius: 'var(--radius-full)',
                      background: statusColor.bg,
                      color: statusColor.color,
                      fontWeight: 600,
                    }}
                  >
                    {isOpen ? 'Open' : 'Closed'}
                  </span>
                </div>

                {/* Row 2: Date/Time */}
                <div
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <NavIcon name="calendar" />
                  {fmtDate(enc.started_at)} · {fmtTime(enc.started_at)}
                </div>

                {/* Row 3: Provider */}
                {enc.provider_name && (
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--text-muted)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <NavIcon name="user" />
                    {enc.provider_name}
                  </div>
                )}

                {/* Row 4: Highlight (vitals summary) */}
                {highlight && (
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--text-muted)',
                      fontStyle: 'normal',
                    }}
                  >
                    {highlight}
                  </div>
                )}

                {/* Row 5: Indicator chips */}
                {/* Row 6: Indicator chips */}
                {counts &&
                  (counts.vitals_count > 0 ||
                    counts.files_count > 0 ||
                    counts.forms_count > 0 ||
                    counts.invoices_count > 0 ||
                    counts.notes_count > 0) && (
                    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginTop: 1 }}>
                      <IndicatorDot icon="activity" count={counts.vitals_count} label="vitals" />
                      <IndicatorDot icon="edit" count={counts.notes_count} label="notes" />
                      <IndicatorDot icon="folder" count={counts.files_count} label="files" />
                      <IndicatorDot icon="file-text" count={counts.forms_count} label="forms" />
                      <IndicatorDot
                        icon="credit-card"
                        count={counts.invoices_count}
                        label="invoices"
                      />
                    </div>
                  )}

                {/* Row 7: Notes preview */}
                {notesMap[enc.id] && notesMap[enc.id].length > 0 && (
                  <div
                    style={{
                      marginTop: 2,
                      padding: '4px 8px',
                      borderRadius: 'var(--radius-sm)',
                      background: 'var(--bg-surface-2, #f5f5f5)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    {notesMap[enc.id].map((n) => (
                      <div
                        key={n.created_at}
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--text)',
                          lineHeight: 1.3,
                        }}
                      >
                        {n.title && <span style={{ fontWeight: 600 }}>{n.title}: </span>}
                        <span style={{ color: 'var(--text-muted)' }}>
                          {(n.body ?? '').slice(0, 60)}
                          {(n.body?.length ?? 0) > 60 ? '…' : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
